/**
 * Alias correctos de series diarias (contrato A9 / unificación de promedios).
 * Antes, fu/pi/pp/rs/cv del endpoint combinado usaban el alias erróneo `AVG_NP`.
 */

export const DAILY_SERIES_FIELD = {
  total_daily: 'AVG_TOTAL',
  nc_daily: 'AVG_NC',
  np_daily: 'AVG_NP',
  fu_daily: 'AVG_FU',
  pi_daily: 'AVG_PI',
  pp_daily: 'AVG_PP',
  rs_daily: 'AVG_RS',
  cv_daily: 'AVG_CV',
}

/** Series tipadas que históricamente llegaban con AVG_NP */
const RENAMED_FROM_AVG_NP = new Set([
  'AVG_FU',
  'AVG_PI',
  'AVG_PP',
  'AVG_RS',
  'AVG_CV',
])

/**
 * Lee el valor numérico de un punto diario con el alias correcto.
 * Si falta el campo nuevo y es una serie renombrada, usa AVG_NP como fallback.
 * @param {Object} item
 * @param {string} fieldName p.ej. 'AVG_FU'
 * @returns {number}
 */
export function readDailyAverageValue(item, fieldName) {
  if (!item || !fieldName) return 0
  const primary = item[fieldName]
  if (primary != null && primary !== '') {
    const n = parseFloat(primary)
    return Number.isFinite(n) ? n : 0
  }
  if (RENAMED_FROM_AVG_NP.has(fieldName) && item.AVG_NP != null && item.AVG_NP !== '') {
    const n = parseFloat(item.AVG_NP)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

/**
 * Normaliza una lista diaria: de-duplica por fecha y renombra AVG_NP → fieldName
 * cuando el alias correcto no viene.
 * @param {Array} list
 * @param {string} valueKey
 * @returns {Array}
 */
export function normalizeDailyAverageList(list, valueKey) {
  if (!Array.isArray(list)) return []
  const pad = n => String(n ?? '').padStart(2, '0')
  const bestByDate = new Map()

  list.forEach(raw => {
    if (!raw) return
    const item = { ...raw }
    // Remap legacy AVG_NP → alias correcto (sin tocar np_daily)
    if (
      valueKey !== 'AVG_NP' &&
      RENAMED_FROM_AVG_NP.has(valueKey) &&
      (item[valueKey] == null || item[valueKey] === '') &&
      item.AVG_NP != null &&
      item.AVG_NP !== ''
    ) {
      item[valueKey] = item.AVG_NP
      delete item.AVG_NP
    }

    const year = item.year ?? item.YYYY ?? ''
    const month = item.month ?? item.MM ?? ''
    const day = item.day ?? item.DD ?? ''
    const dateKey = `${year}-${pad(month)}-${pad(day)}`
    const curVal = readDailyAverageValue(item, valueKey)
    const prev = bestByDate.get(dateKey)
    if (!prev || curVal >= readDailyAverageValue(prev, valueKey)) {
      bestByDate.set(dateKey, item)
    }
  })

  return Array.from(bestByDate.entries())
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([, v]) => v)
}

/**
 * Normaliza todas las series diarias conocidas en `average`.
 * @param {Object} average
 */
export function normalizeAverageDailySeries(average) {
  if (!average || typeof average !== 'object') return
  Object.keys(DAILY_SERIES_FIELD).forEach(key => {
    if (average[key]) {
      average[key] = normalizeDailyAverageList(
        average[key],
        DAILY_SERIES_FIELD[key]
      )
    }
  })
}
